import React, { useState, useEffect, useRef } from 'react';
import { Lock, KeyRound, Eye, EyeOff, ShieldAlert } from 'lucide-react';
import axios from 'axios';
import { config } from '@/config';
import { useAuth } from '@/context/AuthContext';

const API = config.BACKEND_URL;

/**
 * Saisie PIN à 4 cases individuelles
 */
function PinInput({ value, onChange, disabled, autoFocus, onEnter }) {
  const inputs = useRef([]);

  const handleKey = (i, e) => {
    if (e.key === 'Enter') {
      if (onEnter) onEnter();
      return;
    }
    if (e.key === 'Backspace') {
      if (value[i]) {
        const next = value.split('');
        next[i] = '';
        onChange(next.join(''));
      } else if (i > 0) {
        inputs.current[i - 1]?.focus();
        const next = value.split('');
        next[i - 1] = '';
        onChange(next.join(''));
      }
    }
  };

  const handleChange = (i, e) => {
    const char = e.target.value.replace(/\D/g, '').slice(-1);
    if (!char) return;
    const next = value.split('');
    next[i] = char;
    onChange(next.join(''));
    if (i < 3) inputs.current[i + 1]?.focus();
  };

  useEffect(() => {
    if (autoFocus) inputs.current[0]?.focus();
  }, [autoFocus]);

  return (
    <div className="flex gap-3 justify-center">
      {[0, 1, 2, 3].map((i) => (
        <input
          key={i}
          ref={(el) => (inputs.current[i] = el)}
          type="password"
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ''}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKey(i, e)}
          disabled={disabled}
          className="w-12 h-14 text-center text-2xl font-bold rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-[#f76f4b] focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        />
      ))}
    </div>
  );
}

/**
 * Modal double authentification PIN pour l'accès aux pages d'administration.
 * - Mode "verify" : l'utilisateur a déjà un PIN → le saisir
 * - Mode "create" : pas de PIN configuré → créer + confirmer
 */
const AdminPinModal = ({ open, onClose, onSuccess }) => {
  const { user, logout, refreshUser } = useAuth();
  const [mode, setMode] = useState('verify'); // 'verify' | 'create'
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [error, setError] = useState('');
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const [loading, setLoading] = useState(false);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPin('');
    setConfirmPin('');
    setError('');
    setLocked(false);
    setLoading(false);
    setMode(user?.has_admin_pin ? 'verify' : 'create');
    setAttemptsLeft(3);
  }, [open, user?.has_admin_pin]);

  if (!open) return null;

  const handleVerify = async () => {
    if (pin.length !== 4) { setError('Saisissez les 4 chiffres.'); return; }
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/users/${user.id}/verify-admin-pin`, { pin });
      if (data.ok) {
        onSuccess();
      } else if (data.locked) {
        setLocked(true);
        setError('Compte désactivé après 3 tentatives. Contactez un administrateur.');
        await refreshUser();
        setTimeout(() => { logout(); }, 3000);
      } else {
        setAttemptsLeft(data.attempts_left);
        setError(`Code incorrect. ${data.attempts_left} tentative${data.attempts_left > 1 ? 's' : ''} restante${data.attempts_left > 1 ? 's' : ''}.`);
        setPin('');
      }
    } catch {
      setError('Erreur de vérification. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (pin.length !== 4) { setError('Le PIN doit contenir exactement 4 chiffres.'); return; }
    if (pin !== confirmPin) { setError('Les deux codes PIN ne correspondent pas.'); return; }
    setLoading(true);
    setError('');
    try {
      await axios.patch(`${API}/users/${user.id}/admin-pin`, { pin });
      await refreshUser();
      onSuccess();
    } catch {
      setError('Erreur lors de la création du PIN. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Overlay bloquant */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" style={{ zIndex: 9000 }} />

      <div className="fixed inset-0 flex items-center justify-center p-4 pointer-events-none" style={{ zIndex: 9001 }}>
        <div className="pointer-events-auto bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-8 flex flex-col items-center gap-6 text-center">

          {/* Icône */}
          <div className={`p-4 rounded-full ${locked ? 'bg-red-100 dark:bg-red-900' : 'bg-orange-100 dark:bg-orange-900'}`}>
            {locked
              ? <ShieldAlert size={28} className="text-red-500" />
              : <KeyRound size={28} className="text-[#f76f4b]" />
            }
          </div>

          {/* Titre */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {mode === 'verify' ? 'Code PIN requis' : 'Créer votre Code PIN'}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {mode === 'verify'
                ? 'Saisissez votre code PIN pour accéder à l\'administration.'
                : 'Aucun code PIN n\'est configuré. Définissez-en un pour sécuriser l\'accès.'}
            </p>
          </div>

          {/* Saisie PIN */}
          {mode === 'verify' ? (
            <PinInput value={pin} onChange={setPin} disabled={loading || locked} autoFocus={open} onEnter={handleVerify} />
          ) : (
            <div className="flex flex-col gap-4 w-full">
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 text-left">Nouveau PIN</p>
                <PinInput value={pin} onChange={setPin} disabled={loading} autoFocus={open} />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 text-left">Confirmer le PIN</p>
                <PinInput value={confirmPin} onChange={setConfirmPin} disabled={loading} onEnter={handleCreate} />
              </div>
            </div>
          )}

          {/* Message d'erreur */}
          {error && (
            <p className={`text-sm font-medium ${locked ? 'text-red-500' : 'text-red-500'}`}>
              {error}
            </p>
          )}

          {/* Boutons */}
          {!locked && (
            <div className="flex gap-3 w-full">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={mode === 'verify' ? handleVerify : handleCreate}
                disabled={loading || pin.length !== 4 || (mode === 'create' && confirmPin.length !== 4)}
                className="flex-1 px-4 py-2.5 rounded-lg bg-[#f76f4b] hover:bg-[#e55d39] text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? '...' : mode === 'verify' ? 'Valider' : 'Créer le PIN'}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminPinModal;
